import { useEffect } from 'react';

interface UseAudioLevelOptions {
  stream: MediaStream | null;
  onChange: (dbfs: number) => void;
  audioContext?: AudioContext;
}

// Loudness is the RMS of the waveform in dBFS: a mean over the analyser's frequency bins read a
// muted headset's broadband hiss as louder than a shout, because speech fills few of the bins.
export const SILENCE_DBFS = -100;
// About 43 ms at 48 kHz, so each animation frame reads a window longer than the frame itself.
const RMS_WINDOW_SAMPLES = 2048;

export const useAudioLevel = ({
  stream,
  onChange,
  audioContext: suppliedContext,
}: UseAudioLevelOptions) => {
  useEffect(() => {
    let requestId: number;
    let ownContext: AudioContext | undefined;
    let disconnectGraph: (() => void) | undefined;
    let isListening = false;
    if (stream) {
      isListening = true;
      const audioContext = suppliedContext ?? (ownContext = new AudioContext());
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = RMS_WINDOW_SAMPLES;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      disconnectGraph = () => {
        source.disconnect();
        analyser.disconnect();
      };

      const samples = new Float32Array(analyser.fftSize);

      const calculate = () => {
        requestId = window.requestAnimationFrame(calculate);
        analyser.getFloatTimeDomainData(samples);

        let sumOfSquares = 0;
        for (let i = 0; i < samples.length; i++) {
          sumOfSquares += samples[i] * samples[i];
        }

        const rms = Math.sqrt(sumOfSquares / samples.length);
        onChange(Math.max(SILENCE_DBFS, 20 * Math.log10(rms)));
      };

      calculate();
    }

    return () => {
      if (isListening) {
        onChange(SILENCE_DBFS);
        window.cancelAnimationFrame(requestId);
        if (ownContext) {
          ownContext.close();
        } else {
          disconnectGraph?.();
        }
      }
    };
  }, [onChange, stream, suppliedContext]);
};
